import React, { useState, useEffect, useRef } from "react";
import { db } from "../../../services/firebase"; // Prilagodite putanju prema vašoj strukturi
import { collection, getDocs, addDoc } from "firebase/firestore";
import DatePicker, { registerLocale } from "react-datepicker";
import { hr } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faCheck,
  faClock,
  faEnvelope,
  faPhone,
  faUser,
  faStar,
  faCommentDots,
  faTimes,
  faSpinner,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import "../../../styles/pages/booking.css";

interface Service {
  id: string;
  name: string;
  price: number;
  isOnSale: boolean;
  salePrice?: number;
  discountPercentage?: number;
  description?: string;
}

const Booking: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [holidays, setHolidays] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    clientName: "",
    contactNumber: "",
    email: "",
    serviceId: "",
    wantedDate: null as Date | null,
    wantedTimeOfDay: "",
    note: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const successMessageRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Kalendar na hrvatskom jeziku
  registerLocale("hr", hr);

  // Efekt za animaciju elemenata na scroll
  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll(".animate-on-scroll");
      elements.forEach((element) => {
        const position = element.getBoundingClientRect();
        // Provjera je li element vidljiv na ekranu
        if (position.top < window.innerHeight - 100) {
          element.classList.add("animate-visible");
        }
      });
    };

    // Inicijalna provjera vidljivih elemenata
    handleScroll();

    // Dodavanje i uklanjanje event listenera
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesCollection = collection(db, "services");
        const servicesSnapshot = await getDocs(servicesCollection);
        const servicesList = servicesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Service[];
        const sortedServices = servicesList.sort((a, b) =>
          a.name.localeCompare(b.name, "hr")
        );
        setServices(sortedServices);
      } catch (error) {
        console.error("Greška pri dohvaćanju usluga: ", error);
      }
    };

    const fetchHolidays = async () => {
      try {
        // Učitavanje praznika iz kolekcije "croatian_holidays"
        const holidaysSnapshot = await getDocs(
          collection(db, "croatian_holidays")
        );
        const holidayDates = holidaysSnapshot.docs.map(
          (doc) => doc.data().date as string
        );

        // Učitavanje godišnjih odmora iz kolekcije "godisnji_odmor"
        const vacationsSnapshot = await getDocs(
          collection(db, "godisnji_odmor")
        );
        const vacationDates = vacationsSnapshot.docs.flatMap(
          (doc) => doc.data().dates as string[]
        );

        // Kombinacija datuma iz obe kolekcije
        setHolidays([...holidayDates, ...vacationDates]);
      } catch (error) {
        console.error("Greška pri dohvaćanju neradnih dana: ", error);
      }
    };

    fetchServices();
    fetchHolidays();
  }, []);

  useEffect(() => {
    if (successMessage && successMessageRef.current) {
      successMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    setFormData((prevData) => ({ ...prevData, wantedDate: date }));
  };

  const isHolidayOrWeekend = (date: Date) => {
    const day = date.getDay(); // Dohvaća dan u tjednu
    const formattedDate = date.toLocaleDateString("en-CA"); // Lokalno formatiranje u "YYYY-MM-DD"
    return day === 0 || day === 6 || holidays.includes(formattedDate);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.clientName) errors.clientName = "Unesite ime i prezime.";
    if (!formData.contactNumber) errors.contactNumber = "Unesite kontakt broj.";
    if (!formData.email) errors.email = "Unesite email adresu.";
    if (!formData.serviceId) errors.serviceId = "Odaberite uslugu.";
    if (!formData.wantedDate) errors.wantedDate = "Odaberite datum.";
    if (!formData.wantedTimeOfDay)
      errors.wantedTimeOfDay = "Odaberite vrijeme dana.";
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        const selectedService = services.find(
          (service) => service.id === formData.serviceId
        );

        if (!selectedService || !formData.wantedDate) {
          throw new Error("Nedostaju podaci za rezervaciju");
        }

        // Pretvori datum u lokalnu vremensku zonu (bez UTC pomaka)
        const localDate = formData.wantedDate.toLocaleDateString("en-CA"); // Format: YYYY-MM-DD

        await addDoc(collection(db, "online_appointments"), {
          clientName: formData.clientName,
          contactNumber: formData.contactNumber,
          email: formData.email || "",
          serviceId: selectedService.id,
          serviceName: selectedService.name,
          wantedDate: localDate, // Spremi datum u lokalnom formatu
          wantedTimeOfDay: formData.wantedTimeOfDay,
          note: formData.note || "",
          status: "in_progress",
          createdAt: new Date(),
        });

        setFormData({
          clientName: "",
          contactNumber: "",
          email: "",
          serviceId: "",
          wantedDate: null,
          wantedTimeOfDay: "",
          note: "",
        });
        setSuccessMessage(
          "Vaša rezervacija je uspješno poslana! Kontaktirat ćemo Vas uskoro."
        );
        setErrors({});
      } catch (error) {
        console.error("Greška pri slanju rezervacije: ", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="booking-page">
      {/* Hero sekcija */}
      <section className="booking-hero">
        <div className="container">
          <div className="booking-hero-content">
            <h1 className="booking-hero-title">Rezervacija termina</h1>
            <p className="booking-hero-description">
              Rezervirajte svoj termin i prepustite se stručnim rukama našeg
              tima
            </p>
          </div>
        </div>
        <div className="hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,256L48,240C96,224,192,192,288,181.3C384,171,480,181,576,197.3C672,213,768,235,864,224C960,213,1056,171,1152,154.7C1248,139,1344,149,1392,154.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Glavna sekcija za rezervacije */}
      <section className="booking-main">
        {/* Napomene */}
        <div className="services-notes">
          <div className="services-note">
            <FontAwesomeIcon icon={faInfoCircle} className="note-icon" />
            <p>Plaćanje je moguće izvršiti isključivo gotovinom.</p>
          </div>
          <div className="services-note">
            <FontAwesomeIcon icon={faInfoCircle} className="note-icon" />
            <p>
              Nadopuna trepavica vrijedi unutar 4 tjedna, nakon toga računa se
              po cijeni ugradnje.
            </p>
          </div>
          <div className="services-note">
            <FontAwesomeIcon icon={faInfoCircle} className="note-icon" />
            <p>Nadopuna trepavica na tuđi rad računa se po cijeni ugradnje.</p>
          </div>
          <div className="services-note">
            <FontAwesomeIcon icon={faInfoCircle} className="note-icon" />
            <p>
              Propušteni ili prethodno ne otkazani termin (24h ranije) smatra se
              iskorištenim terminom, te ga je prilikom idućeg dolaska potrebno
              podmiriti sa 50% od iznosa propuštenog termina.
            </p>
          </div>
        </div>

        <div className="container">
          <div className="section-header text-center animate-on-scroll">
            <span className="section-subtitle">Online rezervacija</span>
            <h2 className="section-title">Rezervirajte svoj termin</h2>
            <p className="booking-intro">
              Ispunite formu ispod kako biste rezervirali svoj termin.
              Kontaktirat ćemo Vas u najkraćem mogućem roku za potvrdu
              rezervacije.
            </p>
          </div>

          {successMessage && (
            <div
              className="success-message animate-on-scroll"
              ref={successMessageRef}
            >
              <div className="success-icon">
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <div className="success-content">
                <h3>Uspješno poslano!</h3>
                <p>{successMessage}</p>
              </div>
            </div>
          )}

          <div className="booking-content">
            <div className="booking-form-container animate-on-scroll">
              <form onSubmit={handleSubmit} className="booking-form">
                {/* Odabir usluge */}
                <div className="form-group">
                  <label htmlFor="serviceId">
                    <FontAwesomeIcon icon={faStar} className="form-icon" />
                    Odabir usluge <span className="required">*</span>
                  </label>
                  <button
                    type="button"
                    className={`service-selector ${
                      errors.serviceId ? "error" : ""
                    }`}
                    onClick={() => setModalOpen(true)}
                  >
                    {formData.serviceId
                      ? services.find(
                          (service) => service.id === formData.serviceId
                        )?.name
                      : "Odaberite uslugu"}
                  </button>
                  {errors.serviceId && (
                    <div className="error-message">{errors.serviceId}</div>
                  )}
                </div>

                {formData.serviceId && (
                  <div className="selected-service-details animate-on-scroll">
                    {(() => {
                      const selectedService = services.find(
                        (service) => service.id === formData.serviceId
                      );
                      if (!selectedService) return null;

                      return (
                        <>
                          <h4>Detalji odabrane usluge</h4>
                          <div className="service-detail">
                            <span>Naziv:</span>
                            <span>{selectedService.name}</span>
                          </div>
                          <div className="service-detail">
                            <span>Cijena:</span>
                            {selectedService.isOnSale ? (
                              <div className="price-container">
                                <span className="original-price">
                                  {selectedService.price} €
                                </span>
                                <span className="sale-price">
                                  {selectedService.salePrice} €
                                </span>
                                <span className="discount-badge">
                                  -{selectedService.discountPercentage}%
                                </span>
                              </div>
                            ) : (
                              <span>{selectedService.price} €</span>
                            )}
                          </div>
                          {selectedService.description && (
                            <div className="service-detail description">
                              <span>Opis:</span>
                              <span>{selectedService.description}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Ime i prezime */}
                <div className="form-group">
                  <label htmlFor="clientName">
                    <FontAwesomeIcon icon={faUser} className="form-icon" />
                    Ime i prezime <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="clientName"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleInputChange}
                    className={errors.clientName ? "error" : ""}
                    placeholder="Unesite vaše ime i prezime"
                  />
                  {errors.clientName && (
                    <div className="error-message">{errors.clientName}</div>
                  )}
                </div>

                {/* Kontakt broj */}
                <div className="form-group">
                  <label htmlFor="contactNumber">
                    <FontAwesomeIcon icon={faPhone} className="form-icon" />
                    Kontakt broj <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="contactNumber"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleInputChange}
                    className={errors.contactNumber ? "error" : ""}
                    placeholder="Unesite vaš kontakt broj"
                  />
                  {errors.contactNumber && (
                    <div className="error-message">{errors.contactNumber}</div>
                  )}
                </div>

                {/* Email adresa */}
                <div className="form-group">
                  <label htmlFor="email">
                    <FontAwesomeIcon icon={faEnvelope} className="form-icon" />
                    Email adresa <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? "error" : ""}
                    placeholder="Unesite vašu email adresu"
                  />
                  {errors.email && (
                    <div className="error-message">{errors.email}</div>
                  )}
                </div>

                {/* Datum */}
                <div className="form-group">
                  <label htmlFor="wantedDate">
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="form-icon"
                    />
                    Datum rezervacije <span className="required">*</span>
                  </label>
                  <div
                    className={`date-picker-container ${
                      errors.wantedDate ? "error" : ""
                    }`}
                  >
                    <DatePicker
                      selected={formData.wantedDate}
                      onChange={handleDateChange}
                      dateFormat="dd/MM/yyyy"
                      locale="hr"
                      filterDate={(date) => !isHolidayOrWeekend(date)}
                      minDate={new Date()}
                      placeholderText="Odaberite datum"
                      className="date-picker"
                    />
                  </div>
                  {errors.wantedDate && (
                    <div className="error-message">{errors.wantedDate}</div>
                  )}
                </div>

                {/* Vrijeme dana */}
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faClock} className="form-icon" />
                    Vrijeme dana <span className="required">*</span>
                  </label>
                  <div
                    className={`time-selector ${
                      errors.wantedTimeOfDay ? "error" : ""
                    }`}
                  >
                    <label className="time-option">
                      <input
                        type="radio"
                        name="wantedTimeOfDay"
                        value="prijepodne"
                        checked={formData.wantedTimeOfDay === "prijepodne"}
                        onChange={handleInputChange}
                      />
                      <span>Prijepodne</span>
                    </label>
                    <label className="time-option">
                      <input
                        type="radio"
                        name="wantedTimeOfDay"
                        value="poslijepodne"
                        checked={formData.wantedTimeOfDay === "poslijepodne"}
                        onChange={handleInputChange}
                      />
                      <span>Poslijepodne</span>
                    </label>
                  </div>
                  {errors.wantedTimeOfDay && (
                    <div className="error-message">
                      {errors.wantedTimeOfDay}
                    </div>
                  )}
                </div>

                {/* Napomena */}
                <div className="form-group">
                  <label htmlFor="note">
                    <FontAwesomeIcon
                      icon={faCommentDots}
                      className="form-icon"
                    />
                    Napomena
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                    placeholder="Unesite dodatnu napomenu (nije obavezno)"
                  ></textarea>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="submit-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                      <span>Slanje u tijeku...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span>Pošalji zahtjev za rezervaciju</span>
                    </>
                  )}
                </button>
              </form>
            </div>
            <div className="booking-info animate-on-scroll">
              <div className="info-card">
                <h3>Informacije o rezervaciji</h3>
                <ul className="info-list">
                  <li>
                    <FontAwesomeIcon icon={faClock} className="info-icon" />
                    <div className="info-content">
                      <h4>Potvrda termina</h4>
                      <p>
                        Nakon što pošaljete zahtjev za rezervaciju, kontaktirat
                        ćemo vas u najkraćem mogućem roku kako bismo potvrdili
                        točno vrijeme vašeg termina.
                      </p>
                    </div>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="info-icon" />
                    <div className="info-content">
                      <h4>Savjeti za rezervaciju</h4>
                      <p>
                        Preporučujemo da rezervirate termin barem nekoliko dana
                        unaprijed kako bismo vam mogli osigurati željeno
                        vrijeme.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal za odabir usluge */}
      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Odabir usluge</h3>
              <button
                className="close-button"
                onClick={() => setModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-content">
              <ul className="services-list">
                {services.map((service) => (
                  <li
                    key={service.id}
                    className="service-item"
                    onClick={() => {
                      setFormData((prevData) => ({
                        ...prevData,
                        serviceId: service.id,
                      }));
                      setModalOpen(false);
                    }}
                  >
                    <div className="service-name">{service.name}</div>
                    <div className="service-price">
                      {service.isOnSale ? (
                        <>
                          <span className="original-price">
                            {service.price}€
                          </span>
                          <span className="sale-price">
                            {service.salePrice}€
                          </span>
                          <span className="discount">
                            -{service.discountPercentage}%
                          </span>
                        </>
                      ) : (
                        `${service.price}€`
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="modal-footer">
              <button
                className="close-modal-button"
                onClick={() => setModalOpen(false)}
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Booking;
